import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InventoryApiService, InventoryApiItem } from '../../shared/inventory-api.service';

interface InventoryItem {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: string;
  currency: string;
  imageUrl?: string;
}

type StockFilter = 'All Stock' | 'In Stock' | 'Low Stock' | 'Out of Stock';

const STARTER_INVENTORY: InventoryItem[] = [
  { id: 'SKU-1001', name: 'Starter Chatbot Plan', price: 299, stock: 46, category: 'Subscription', currency: 'USD' },
  { id: 'SKU-1002', name: 'Growth Chatbot Plan', price: 599, stock: 28, category: 'Subscription', currency: 'USD' },
  { id: 'SKU-1003', name: 'Enterprise Support Add-on', price: 199, stock: 17, category: 'Support', currency: 'USD' },
  { id: 'SKU-1004', name: 'WhatsApp Template Bundle', price: 79, stock: 96, category: 'Templates', currency: 'USD' },
  { id: 'SKU-1005', name: 'CRM Connector Pack', price: 149, stock: 24, category: 'Integrations', currency: 'USD' },
  { id: 'SKU-1006', name: 'Analytics Dashboard License', price: 229, stock: 34, category: 'Analytics', currency: 'USD' },
  { id: 'SKU-1007', name: 'Broadcast Credits - 10k', price: 119, stock: 75, category: 'Credits', currency: 'USD' },
  { id: 'SKU-1008', name: 'Broadcast Credits - 50k', price: 449, stock: 19, category: 'Credits', currency: 'USD' },
  { id: 'SKU-1009', name: 'Flow Builder Pro', price: 269, stock: 21, category: 'Automation', currency: 'USD' },
  { id: 'SKU-1010', name: 'Human Agent Seat', price: 39, stock: 132, category: 'Seats', currency: 'USD' },
  { id: 'SKU-1011', name: 'WhatsApp Number Warmup', price: 89, stock: 12, category: 'Onboarding', currency: 'USD' },
  { id: 'SKU-1012', name: 'Compliance Audit Pack', price: 159, stock: 9, category: 'Compliance', currency: 'USD' }
];

@Component({
  standalone: true,
  selector: 'app-inventory-page',
  imports: [CommonModule, FormsModule],
  template: `
  <div class="page">
    <div class="hero">
      <div class="ic">📦</div>
      <div>
        <div class="h1">Inventory</div>
        <div class="h2">Manage product catalog with add, edit, delete and bulk upload</div>
      </div>
    </div>

    <div class="stats">
      <div class="stat">
        <div class="k">Products</div>
        <div class="v">{{ filteredItems().length }}</div>
      </div>
      <div class="stat">
        <div class="k">Total Stock</div>
        <div class="v">{{ totalStock() }}</div>
      </div>
      <div class="stat">
        <div class="k">Low Stock (&lt; 10)</div>
        <div class="v">{{ lowStockCount() }}</div>
      </div>
      <div class="stat">
        <div class="k">Inventory Value</div>
        <div class="v">{{ money(inventoryValue()) }}</div>
      </div>
    </div>

    <div class="card controls">
      <div class="topRow">
        <div class="searchWrap">
          <input
            type="text"
            [ngModel]="searchText()"
            (ngModelChange)="onSearchChange($event)"
            placeholder="Search by id, name or category" />
        </div>
        <div class="stockFilter">
          <span class="muted">Stock</span>
          <div class="chips">
            <button
              type="button"
              class="chip"
              *ngFor="let option of stockFilterOptions"
              [class.active]="selectedStockFilter()===option"
              (click)="onStockFilterChange(option)">
              {{ option }}
            </button>
          </div>
        </div>
        <div class="uploadWrap">
          <label class="uploadBtn">
            Upload CSV/Excel-export
            <input type="file" accept=".csv,.tsv,.txt,.xls,.xlsx" (change)="onImportFile($event)" />
          </label>
        </div>
      </div>

      <div class="formGrid">
        <input type="text" [(ngModel)]="draft.id" placeholder="Product ID (e.g., SKU-5001)" />
        <input type="text" [(ngModel)]="draft.name" placeholder="Product Name" />
        <input type="number" [(ngModel)]="draft.price" placeholder="Price" min="0" />
        <input type="number" [(ngModel)]="draft.stock" placeholder="Stock" min="0" />
        <input type="text" [(ngModel)]="draft.category" placeholder="Category" />
        <input type="text" [(ngModel)]="draft.imageUrl" placeholder="Image URL" />
        <select [(ngModel)]="draft.currency">
          <option *ngFor="let c of currencyOptions" [value]="c">{{ c }}</option>
        </select>
        <div class="formActions">
          <button type="button" class="btn primary" (click)="saveDraft()">{{ editingId() ? 'Update Product' : 'Add Product' }}</button>
          <button type="button" class="btn" (click)="resetDraft()">Clear</button>
        </div>
      </div>
      <div class="msg" *ngIf="message()">{{ message() }}</div>
      <div class="msg" *ngIf="loading()">Syncing with backend...</div>
    </div>

    <div class="card list">
      <div class="listTop">
        <div class="muted">Showing {{ pageStartIndex() }}-{{ pageEndIndex() }} of {{ filteredItems().length }}</div>
        <div class="pageSizeWrap">
          <label for="pageSize" class="muted">Rows</label>
          <select id="pageSize" class="pageSizeSelect" [value]="pageSize()" (change)="onPageSizeSelect($event)">
            <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
          </select>
        </div>
      </div>
      <div class="tableWrap">
        <div class="head">
          <div>ID</div>
          <div>Name</div>
          <div>Category</div>
          <div>Image</div>
          <div>Price</div>
          <div>Stock</div>
          <div>Currency</div>
          <div>Actions</div>
        </div>
        <div class="row" *ngFor="let item of pagedItems()">
          <div class="mono idCell">{{ item.id }}</div>
          <div class="name">{{ item.name }}</div>
          <div class="muted">{{ item.category }}</div>
          <div>
            <img *ngIf="item.imageUrl" class="thumb" [src]="item.imageUrl" [alt]="item.name" />
            <span *ngIf="!item.imageUrl" class="muted">-</span>
          </div>
          <div class="name">{{ money(item.price, item.currency) }}</div>
          <div>
            <span class="stock" [class.low]="item.stock < 10">{{ item.stock }}</span>
          </div>
          <div class="muted">{{ item.currency }}</div>
          <div class="actions">
            <button type="button" class="smallBtn" (click)="editItem(item)">Edit</button>
            <button type="button" class="smallBtn danger" (click)="deleteItem(item.id)">Delete</button>
          </div>
        </div>
        <div class="empty" *ngIf="!filteredItems().length">No products found.</div>
      </div>
      <div class="pager" *ngIf="filteredItems().length">
        <button type="button" class="pagerBtn" [disabled]="effectivePage() <= 1" (click)="goToPrevPage()">Previous</button>
        <div class="muted pageInfo">Page {{ effectivePage() }} / {{ totalPages() }}</div>
        <button type="button" class="pagerBtn" [disabled]="effectivePage() >= totalPages()" (click)="goToNextPage()">Next</button>
      </div>
    </div>
  </div>
  `,
  styles: [`
    .page{flex:1; padding:16px; overflow:auto}
    .hero{display:flex; gap:12px; align-items:center; padding:14px; border-radius:18px;border:1px solid var(--border); background:rgba(17,27,33,.55)}
    .ic{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;background:rgba(0,168,132,.16);border:1px solid rgba(0,168,132,.35);font-size:20px}
    .h1{font-size:18px;font-weight:900}
    .h2{margin-top:2px;color:var(--muted);font-size:12px}
    .stats{margin-top:12px;display:grid;gap:12px;grid-template-columns:repeat(4,minmax(0,1fr))}
    .stat{padding:12px;border-radius:16px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .k{font-size:12px;color:var(--muted)}
    .v{font-size:24px;font-weight:900;margin-top:4px}
    .card{margin-top:12px;padding:12px;border-radius:18px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .controls{display:grid;gap:10px}
    .topRow{display:flex;gap:10px;align-items:center;justify-content:space-between}
    .searchWrap{flex:1}
    .searchWrap input{width:100%;height:38px;border-radius:10px;border:1px solid var(--border);background:var(--input);color:var(--text);padding:0 10px;outline:none}
    .stockFilter{display:flex;align-items:center;gap:8px}
    .chips{display:flex;gap:6px;flex-wrap:wrap}
    .chip{
      border:1px solid var(--border);background:transparent;color:var(--muted);border-radius:999px;padding:4px 10px;
      font-size:12px;cursor:pointer
    }
    .chip:hover{color:var(--text);border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.08)}
    .chip.active{color:var(--text);border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.16)}
    .uploadBtn{display:inline-flex;align-items:center;justify-content:center;height:38px;padding:0 12px;border-radius:10px;border:1px solid rgba(0,168,132,.45);background:rgba(0,168,132,.16);cursor:pointer;font-size:12px;font-weight:700}
    .uploadBtn input{display:none}
    .formGrid{display:grid;gap:8px;grid-template-columns:1.1fr 1.4fr .8fr .8fr 1fr 1.3fr .7fr auto}
    .formGrid input,.formGrid select{height:38px;border-radius:10px;border:1px solid var(--border);background:var(--input);color:var(--text);padding:0 10px;outline:none}
    .formActions{display:flex;gap:8px}
    .btn{height:38px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text);padding:0 12px;cursor:pointer}
    .btn.primary{border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.18)}
    .msg{font-size:12px;color:var(--muted)}
    .listTop{display:flex;justify-content:space-between;align-items:center;padding:6px 2px}
    .pageSizeWrap{display:flex;align-items:center;gap:8px}
    .pageSizeSelect{height:32px;border-radius:10px;border:1px solid var(--border);background:var(--input);color:var(--text);padding:0 8px}
    .tableWrap{max-height:min(56vh,580px);overflow:auto}
    .head,.row{display:grid;grid-template-columns:120px 1.6fr 1.1fr .8fr .9fr .7fr .8fr 1fr;gap:10px;align-items:center}
    .head{position:sticky;top:0;z-index:1;background:rgba(17,27,33,.96);padding:10px 8px;font-size:11px;color:var(--muted);font-weight:700;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid rgba(42,57,66,.45)}
    .row{padding:10px 8px;border-radius:10px}
    .row:hover{background:rgba(42,57,66,.35)}
    .mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:700;font-size:12px}
    .idCell{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .name{font-weight:800}
    .muted{color:var(--muted)}
    .stock{display:inline-flex;padding:2px 8px;border-radius:999px;border:1px solid rgba(16,185,129,.4);background:rgba(16,185,129,.16);font-size:11px;font-weight:800}
    .stock.low{border-color:rgba(244,63,94,.4);background:rgba(244,63,94,.16);color:#fda4af}
    .thumb{width:34px;height:34px;border-radius:8px;object-fit:cover;border:1px solid rgba(42,57,66,.65);display:block}
    .actions{display:flex;gap:8px}
    .smallBtn{height:30px;border-radius:9px;border:1px solid var(--border);background:transparent;color:var(--text);padding:0 10px;cursor:pointer;font-size:12px}
    .smallBtn.danger{border-color:rgba(244,63,94,.4);color:#fda4af}
    .empty{padding:12px;text-align:center;color:var(--muted)}
    .pager{margin-top:10px;display:flex;justify-content:center;gap:10px;align-items:center;padding:8px 10px}
    .pagerBtn{height:34px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text);padding:0 12px;cursor:pointer;font-size:12px}
    .pagerBtn:hover{border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.10)}
    .pagerBtn:disabled{opacity:.45;cursor:not-allowed}
    .pageInfo{min-width:90px;text-align:center}
    :host-context(body.light) .hero,:host-context(body.light) .stat,:host-context(body.light) .card{background:rgba(255,255,255,.92);border-color:rgba(15,23,42,.10)}
    :host-context(body.light) .head{background:rgba(255,255,255,.98);border-bottom-color:rgba(15,23,42,.10)}
    :host-context(body.light) .row:hover{background:rgba(15,23,42,.04)}
    :host-context(body.light) .chip{
      color:#475569;
      border-color:rgba(15,23,42,.14);
      background:rgba(15,23,42,.02);
    }
    :host-context(body.light) .chip:hover{color:#0f172a;background:rgba(2,132,199,.08);border-color:rgba(2,132,199,.22)}
    :host-context(body.light) .chip.active{color:#0f172a;background:rgba(2,132,199,.14);border-color:rgba(2,132,199,.30)}
    :host-context(body.light) .h1,:host-context(body.light) .name,:host-context(body.light) .v{color:#0f172a}
    :host-context(body.light) .muted,:host-context(body.light) .k,:host-context(body.light) .h2,:host-context(body.light) .empty,:host-context(body.light) .msg{color:#475569}
    :host-context(body.light) input,:host-context(body.light) select,:host-context(body.light) .pageSizeSelect{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.16)}
    @media (max-width: 1200px){
      .stats{grid-template-columns:repeat(2,minmax(0,1fr))}
      .formGrid{grid-template-columns:1fr 1fr 1fr 1fr}
      .formActions{grid-column:1 / -1}
    }
    @media (max-width: 900px){
      .topRow{flex-direction:column;align-items:stretch}
      .head{display:none}
      .row{grid-template-columns:1fr;gap:6px}
      .stats{grid-template-columns:1fr 1fr}
    }
  `]
})
export class InventoryPageComponent implements OnInit {
  items = signal<InventoryItem[]>(STARTER_INVENTORY);
  searchText = signal<string>('');
  selectedStockFilter = signal<StockFilter>('All Stock');
  editingId = signal<string | null>(null);
  message = signal<string>('');
  loading = signal<boolean>(false);

  pageSizeOptions = [5, 10, 20];
  stockFilterOptions: StockFilter[] = ['All Stock', 'In Stock', 'Low Stock', 'Out of Stock'];
  pageSize = signal<number>(10);
  currentPage = signal<number>(1);
  currencyOptions = ['USD', 'EUR', 'INR', 'AED'];

  draft: InventoryItem = this.emptyDraft();

  constructor(private readonly inventoryApi: InventoryApiService) {}

  async ngOnInit(): Promise<void> {
    await this.reloadInventory();
  }

  filteredItems = computed(() => {
    const q = this.searchText().trim().toLowerCase();
    const stockFilter = this.selectedStockFilter();

    return this.items().filter(item => {
      const matchesQuery = !q
        || item.id.toLowerCase().includes(q)
        || item.name.toLowerCase().includes(q)
        || item.category.toLowerCase().includes(q);
      if (!matchesQuery) return false;

      if (stockFilter === 'In Stock') return item.stock > 0;
      if (stockFilter === 'Low Stock') return item.stock > 0 && item.stock < 10;
      if (stockFilter === 'Out of Stock') return item.stock === 0;
      return true;
    });
  });

  totalStock = computed(() => this.filteredItems().reduce((sum, item) => sum + item.stock, 0));
  inventoryValue = computed(() => this.filteredItems().reduce((sum, item) => sum + (item.price * item.stock), 0));
  lowStockCount = computed(() => this.filteredItems().filter(item => item.stock < 10).length);

  totalPages = computed(() => Math.max(1, Math.ceil(this.filteredItems().length / this.pageSize())));
  effectivePage = computed(() => Math.min(this.currentPage(), this.totalPages()));
  pagedItems = computed(() => {
    const start = (this.effectivePage() - 1) * this.pageSize();
    return this.filteredItems().slice(start, start + this.pageSize());
  });
  pageStartIndex = computed(() => this.filteredItems().length ? ((this.effectivePage() - 1) * this.pageSize()) + 1 : 0);
  pageEndIndex = computed(() => Math.min(this.effectivePage() * this.pageSize(), this.filteredItems().length));

  async saveDraft(): Promise<void> {
    const normalized = this.normalizeDraft(this.draft);
    if (!normalized) {
      this.message.set('Please provide valid values for all fields.');
      return;
    }

    const list = this.items();
    const existingIdx = list.findIndex(item => item.id.toLowerCase() === normalized.id.toLowerCase());
    const activeEditId = this.editingId();

    if (!activeEditId && existingIdx !== -1) {
      this.message.set(`Product ID "${normalized.id}" already exists.`);
      return;
    }

    if (activeEditId) {
      const ok = window.confirm(`Update product "${activeEditId}"?`);
      if (!ok) {
        this.message.set('Update cancelled.');
        return;
      }
    }

    try {
      this.loading.set(true);
      await this.inventoryApi.upsertInventory(normalized.id, this.toApiItem(normalized));
      if (activeEditId) {
        this.items.update(current =>
          current.map(item => item.id === activeEditId ? normalized : item)
        );
        this.message.set(`Updated ${normalized.id}`);
      } else {
        this.items.update(current => [normalized, ...current]);
        this.message.set(`Added ${normalized.id}`);
      }
      this.resetDraft();
    } catch (error) {
      this.message.set(`Save failed for ${normalized.id}.`);
      console.error('Inventory save failed', error);
    } finally {
      this.loading.set(false);
    }
  }

  editItem(item: InventoryItem): void {
    this.editingId.set(item.id);
    this.draft = { ...item };
    this.message.set(`Editing ${item.id}`);
  }

  async deleteItem(id: string): Promise<void> {
    const ok = window.confirm(`Delete product "${id}"? This cannot be undone.`);
    if (!ok) {
      this.message.set('Delete cancelled.');
      return;
    }
    try {
      this.loading.set(true);
      await this.inventoryApi.deleteInventory(id);
      this.items.update(current => current.filter(item => item.id !== id));
      if (this.editingId() === id) this.resetDraft();
      this.message.set(`Deleted ${id}`);
    } catch (error) {
      this.message.set(`Delete failed for ${id}.`);
      console.error('Inventory delete failed', error);
    } finally {
      this.loading.set(false);
    }
  }

  resetDraft(): void {
    this.draft = this.emptyDraft();
    this.editingId.set(null);
  }

  onSearchChange(value: string): void {
    this.searchText.set(value || '');
    this.currentPage.set(1);
  }

  onStockFilterChange(value: StockFilter): void {
    this.selectedStockFilter.set(value);
    this.currentPage.set(1);
  }

  onPageSizeSelect(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const selected = Number(target?.value);
    if (!selected || !this.pageSizeOptions.includes(selected)) return;
    this.pageSize.set(selected);
    this.currentPage.set(1);
  }

  goToPrevPage(): void {
    if (this.effectivePage() <= 1) return;
    this.currentPage.update(page => page - 1);
  }

  goToNextPage(): void {
    if (this.effectivePage() >= this.totalPages()) return;
    this.currentPage.update(page => page + 1);
  }

  async onImportFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;

    const content = await this.readFileAsText(file);
    const parsed = this.parseDelimitedInventory(content);
    if (!parsed.length) {
      this.message.set('Import failed. Use CSV/TSV with headers: id,name,price,stock,category,currency,imageUrl');
      input!.value = '';
      return;
    }

    try {
      this.loading.set(true);
      let added = 0;
      let updated = 0;
      const existing = new Set(this.items().map(item => item.id.toLowerCase()));

      for (const row of parsed) {
        if (existing.has(row.id.toLowerCase())) updated += 1;
        else added += 1;
        await this.inventoryApi.upsertInventory(row.id, this.toApiItem(row));
      }

      await this.reloadInventory();
      this.message.set(`Imported ${parsed.length} rows (${added} added, ${updated} updated).`);
    } catch (error) {
      this.message.set('Import failed while syncing with backend.');
      console.error('Inventory import failed', error);
    } finally {
      this.loading.set(false);
      input.value = '';
    }
  }

  money(amount: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  }

  private emptyDraft(): InventoryItem {
    return { id: '', name: '', price: 0, stock: 0, category: '', currency: 'USD', imageUrl: '' };
  }

  private toApiItem(item: InventoryItem): InventoryApiItem {
    return {
      id: item.id,
      name: item.name,
      category: item.category,
      stock: item.stock,
      price: item.price,
      currency: item.currency,
      imageUrl: item.imageUrl || '',
    };
  }

  private normalizeDraft(input: InventoryItem): InventoryItem | null {
    const id = String(input.id || '').trim();
    const name = String(input.name || '').trim();
    const category = String(input.category || '').trim();
    const currency = String(input.currency || '').trim().toUpperCase();
    const imageUrl = String(input.imageUrl || '').trim();
    const price = Number(input.price);
    const stock = Number(input.stock);

    if (!id || !name || !category || !currency) return null;
    if (!Number.isFinite(price) || price < 0) return null;
    if (!Number.isFinite(stock) || stock < 0) return null;

    return { id, name, category, currency, imageUrl, price, stock };
  }

  private async readFileAsText(file: File): Promise<string> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  private parseDelimitedInventory(raw: string): InventoryItem[] {
    const lines = raw
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);
    if (lines.length < 2) return [];

    const delimiter = this.detectDelimiter(lines[0]);
    const headers = lines[0].split(delimiter).map(h => this.normalizeHeader(h));
    const idx = {
      id: headers.indexOf('id'),
      name: headers.indexOf('name'),
      price: headers.indexOf('price'),
      stock: headers.indexOf('stock'),
      category: headers.indexOf('category'),
      currency: headers.indexOf('currency'),
      imageUrl: headers.indexOf('imageurl')
    };
    if ([idx.id, idx.name, idx.price, idx.stock, idx.category, idx.currency].some(v => v === -1)) return [];

    const out: InventoryItem[] = [];
    for (let i = 1; i < lines.length; i += 1) {
      const cols = lines[i].split(delimiter).map(col => col.trim().replace(/^"|"$/g, ''));
      const row: InventoryItem = {
        id: cols[idx.id] || '',
        name: cols[idx.name] || '',
        price: Number(cols[idx.price] || 0),
        stock: Number(cols[idx.stock] || 0),
        category: cols[idx.category] || '',
        currency: (cols[idx.currency] || 'USD').toUpperCase(),
        imageUrl: idx.imageUrl >= 0 ? (cols[idx.imageUrl] || '') : ''
      };
      const normalized = this.normalizeDraft(row);
      if (normalized) out.push(normalized);
    }
    return out;
  }

  private detectDelimiter(line: string): string {
    const commas = (line.match(/,/g) || []).length;
    const tabs = (line.match(/\t/g) || []).length;
    return tabs > commas ? '\t' : ',';
  }

  private normalizeHeader(header: string): string {
    return header
      .trim()
      .toLowerCase()
      .replaceAll('_', '')
      .replaceAll(' ', '');
  }

  private async reloadInventory(): Promise<void> {
    try {
      this.loading.set(true);
      const rows = await this.inventoryApi.listInventory();
      if (!rows.length) {
        this.items.set([]);
        this.message.set('No inventory data returned from backend.');
        return;
      }
      this.items.set(rows.map(row => ({
        id: row.id,
        name: row.name,
        category: row.category,
        stock: row.stock,
        price: row.price,
        currency: row.currency || 'USD',
        imageUrl: row.imageUrl || '',
      })));
      this.message.set(`Loaded ${rows.length} products from backend.`);
    } catch (error) {
      this.message.set('Failed to load inventory from backend.');
      console.error('Inventory load failed', error);
    } finally {
      this.loading.set(false);
    }
  }
}
